import React, { createContext, useState, useContext, useRef, useEffect } from 'react'
import { OrderEngineContext } from './OrderEngineContext'
import axios from 'axios'
import okeResponse from 'okeoke.standardresponse'
import SocketIOClient from 'socket.io-client'

const OkeVipCustomerContext = createContext()

const vipUrl = 'https://vip.okeoke.io'
const vipUrlDev = 'https://vipdev.okeoke.io'

const OkeVipCustomerProvider = (props) => {
  const { brandID, token, apiUrl } = useContext(OrderEngineContext)
  const tokenRef = useRef(null)
  const brandIDRef = useRef(null)
  const ioRef = useRef(null)
  const [vipConnected, setVipConnected] = useState(false)
  const [vipLoggedIn, setVipLoggedIn] = useState(false)
  const [vipParams, setVipParams] = useState(null)

  const vipUrlRef = useRef(vipUrlDev)

  const getVipParams = async (_brandID) => {
    try {
      const response = await axios(vipUrlRef.current +  '/api/params', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'brandid': _brandID,
        }
      })
      if(response.data.success) {
        vipUrlRef.current = response.data.data?.isDev ? vipUrlDev : vipUrl
        setVipParams(response.data.data?.params || {})
      }
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const getVipProfile = async () => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/profile', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        }
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const setVipProfile = async (params) => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/profile', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        },
        data: JSON.stringify({ params }),
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const setVipReferredBy = async (referredByID) => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/referredBy', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        },
        data: JSON.stringify({ referredByID: referredByID || null }),
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const getCoupons = async () => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/coupons', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        }
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const activateCoupon = async (couponCode, deactivate) => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/activateCoupon', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        },
        data: JSON.stringify({ 
          couponCode,
          deactivate
        }),
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const claimCoupon = async (couponCode) => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/claimCoupon', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        },
        data: JSON.stringify({ 
          couponCode
        }),
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const setUsePoints = async (amount) => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/usePoints', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        },
        data: JSON.stringify({ 
          amount
        }),
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const payOrder = async (orderUUID) => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      let res = await axios(`${apiUrl}/api/v1/payment/vip/start`, {
        method: 'POST',
        data: JSON.stringify({ orderUUID }),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      })
      return res.data
    } catch (e) {
      return { success: false, code: 500 }
    }
  }

  const getVipAchievements = async () => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/achievements', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        }
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const getVipPurchases = async (fromTimestamp = null) => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/purchases' + fromTimestamp ? '?fromTimestamp=' + fromTimestamp : '', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        }
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const getVipPurchase = async (purchaseUUID = null) => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      if(!purchaseUUID) return okeResponse(false, 401, 'Purchase UUID not provided.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/purchases/' + purchaseUUID, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        }
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const getVipTransactions = async (fromTimestamp = null) => {
    try {
      if(!vipParams) return okeResponse(false, 500, 'Vip not available.', null)
      const response = await axios(vipUrlRef.current +  '/api/customer/transactions' + fromTimestamp ? '?fromTimestamp=' + fromTimestamp : '', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tokenRef.current,
          'brandid': brandIDRef.current,
        }
      })
      return response.data
    } catch (e) {
      return okeResponse(false, 500, 'Internal error.', e)
    }
  }

  const onConnectionChange = (data) => {
    switch (data.state) {
      case 'Connected': {
        setVipConnected(true)
      }
        break
      case 'Disconnected': {
        setVipConnected(false)
        setVipLoggedIn(false)
      }
        break
    }
  }

  useEffect(() => {
    if(brandID) getVipParams(brandID)
  }, [brandID])

  useEffect(() => {
    tokenRef.current = token
    brandIDRef.current = brandID
  }, [brandID, token])

  useEffect(() => {
    if (!ioRef.current && token && brandID && vipParams) {
      let ioClient = SocketIOClient(vipUrlRef.current, {
        path: '/vip.sock',
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 0
      })
      ioClient.on('connect', () => {
        ioClient.emit('login', {
          brandID: brandID,
          token: token,
          type: 'customer',
        }, (data) => {
          //console.log('login res:', data)
          if(data.success) {
            ioClient.emit('ready', { }, (data) => {
              //console.log('ready res:', data)
            })
            setVipLoggedIn(true)
          }
        })
        onConnectionChange({ state: 'Connected', message: '' })
      })
      ioClient.on('disconnect', () => {
        onConnectionChange({ state: 'Disconnected', message: '' })
      })
      ioClient.on('error', (error) => {
        onConnectionChange({ state: 'Disconnected', message: error })
      })
      ioClient.on('reconnect', () => {
      })
      ioRef.current = ioClient
    }
  }, [token, brandID, vipParams])

  return (
    <OkeVipCustomerContext.Provider
      value={{
        getVipProfile,
        setVipProfile,
        setVipReferredBy,
        getCoupons,
        activateCoupon,
        claimCoupon,
        setUsePoints,
        payOrder,
        getVipAchievements,
        getVipPurchases,
        getVipPurchase,
        getVipTransactions,
        vipConnected,
        vipLoggedIn,
        vipParams
      }}
    >
      {props.children}
    </OkeVipCustomerContext.Provider>
  )
}

export { OkeVipCustomerContext, OkeVipCustomerProvider }
